import Add from "@mui/icons-material/Add";
import ArrowUpward from "@mui/icons-material/ArrowUpward";
import AttachFile from "@mui/icons-material/AttachFile";
import Close from "@mui/icons-material/Close";
import DarkModeOutlined from "@mui/icons-material/DarkModeOutlined";
import GraphicEq from "@mui/icons-material/GraphicEq";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUp from "@mui/icons-material/KeyboardArrowUp";
import LightModeOutlined from "@mui/icons-material/LightModeOutlined";
import Mic from "@mui/icons-material/Mic";
import RefreshOutlined from "@mui/icons-material/RefreshOutlined";
import Stop from "@mui/icons-material/Stop";
import StopCircleOutlined from "@mui/icons-material/StopCircleOutlined";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import axios from "axios";
import DOMPurify from "dompurify";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { DOCUMENT_ACCEPT_TYPES, isAllowedDocumentType } from "../constants";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useWhitelabel } from "../context/WhitelabelContext";
import { useDictation } from "../hooks/useDictation";
import { getInstanceConfig, getLogoMode, hostname } from "../utils";

interface AgentConfig {
  id: string;
  name: string;
  avatarImage?: string;
  customColors?: {
    primary?: string;
    secondary?: string;
  };
  channelSettings?: {
    website?: {
      enabled?: boolean;
      welcomeMessage?: string;
      placeholder?: string;
      streamingEnabled?: boolean;
      multiMessageEnabled?: boolean;
    };
    webpage?: {
      enabled?: boolean;
      welcomeMessage?: string;
      placeholder?: string;
      description?: string;
      contactForm?: boolean;
      streamingEnabled?: boolean;
      voiceEnabled?: boolean;
    };
    voice?: {
      enabled?: boolean;
      elevenLabsVoiceId?: string;
    };
  };
  placementSettings?: {
    welcomeMessage?: string;
    placeholder?: string;
  };
  isActive: boolean;
  leadFields?: Array<{
    key: string;
    label: string;
    type: string;
    required: boolean;
  }>;
  contactListId?: string;
  shortCode?: string;
}

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: number;
  attachments?: Array<{ name: string; type: string }>;
  isStreaming?: boolean;
}

interface Attachment {
  name: string;
  content: string;
  type: string;
}

const ShareableAgentPage: React.FC<{ mode: string }> = ({ mode: initialMode }) => {
  const { shortCode } = useParams<{ shortCode: string }>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { whitelabelSettings, whitelabelLogos } = useWhitelabel();

  const [agent, setAgent] = useState<AgentConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [currentMode, setCurrentMode] = useState(initialMode);
  const [contactInfo, setContactInfo] = useState<Record<string, string>>({});
  const [contactFormSubmitted, setContactFormSubmitted] = useState(false);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [descriptionOverflows, setDescriptionOverflows] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const streamingContentRef = useRef<HTMLDivElement>(null);
  const lastScrollTimeRef = useRef<number>(0);
  const isStreamingRef = useRef<boolean>(false);

  const isDark = currentMode === "dark";

  // Dictation hook for speech-to-text
  const {
    isRecording: isDictating,
    isLoading: isDictationLoading,
    startDictation,
    stopDictation,
  } = useDictation({
    onTranscript: (text, isFinal) => {
      if (isFinal && text.trim()) {
        setInputValue((prev) => {
          const trimmedPrev = prev.trim();
          const trimmedText = text.trim();
          return trimmedPrev ? `${trimmedPrev} ${trimmedText}` : trimmedText;
        });
      }
    },
    onError: (error) => {
      console.error("Dictation error:", error);
    },
  });

  // Voice chat state
  const [isVoiceChatActive, setIsVoiceChatActive] = useState(false);
  const audioQueueRef = useRef<{ url: string; text: string }[]>([]);
  const isPlayingAudioRef = useRef(false);
  const audioPlayerRef = useRef<HTMLAudioElement | null>(null);
  const isVoiceChatActiveRef = useRef(false);
  const isVoiceProcessingRef = useRef(false);

  // Voice chat dictation hook - separate from regular dictation
  const {
    isRecording: isVoiceRecording,
    startDictation: startVoiceDictation,
    stopDictation: stopVoiceDictation,
  } = useDictation({
    onTranscript: (text, isFinal) => {
      if (isFinal && text.trim() && isVoiceChatActiveRef.current && !isVoiceProcessingRef.current) {
        // Stop dictation immediately to prevent duplicate inputs
        stopVoiceDictation();
        sendVoiceMessage(text.trim());
      }
    },
    onError: (error) => {
      console.error("Voice chat error:", error);
      setIsVoiceChatActive(false);
      isVoiceChatActiveRef.current = false;
    },
    provider: "deepgram",
  });

  // Store dictation functions in refs for stable access in callbacks
  const stopVoiceDictationRef = useRef(stopVoiceDictation);
  stopVoiceDictationRef.current = stopVoiceDictation;
  const startVoiceDictationRef = useRef(startVoiceDictation);
  startVoiceDictationRef.current = startVoiceDictation;

  // Check if voice chat is available for this agent
  const isVoiceChatAvailable =
    agent?.channelSettings?.webpage?.voiceEnabled &&
    agent?.channelSettings?.voice?.enabled &&
    agent?.channelSettings?.voice?.elevenLabsVoiceId;

  // Play audio queue for voice chat
  const playNextAudio = useCallback(() => {
    if (audioQueueRef.current.length === 0) {
      isPlayingAudioRef.current = false;
      // Start listening again after audio finishes (if still in voice chat mode and not processing)
      if (isVoiceChatActiveRef.current && !isVoiceProcessingRef.current) {
        startVoiceDictationRef.current();
      }
      return;
    }

    const next = audioQueueRef.current.shift()!;
    audioPlayerRef.current = new Audio(next.url);
    audioPlayerRef.current.onended = () => {
      URL.revokeObjectURL(next.url);
      playNextAudio();
    };
    audioPlayerRef.current.onerror = () => {
      URL.revokeObjectURL(next.url);
      playNextAudio();
    };
    audioPlayerRef.current.play().catch((e) => {
      console.error("Audio playback failed:", e);
      URL.revokeObjectURL(next.url);
      playNextAudio();
    });
  }, []);

  // Send voice message with audio response
  const sendVoiceMessage = async (text: string) => {
    if (!text.trim() || !agent || !sessionId) return;

    isVoiceProcessingRef.current = true;
    setIsProcessing(true);

    const userMessage: Message = {
      id: `user_${Date.now()}`,
      role: "user",
      content: text,
      timestamp: Date.now(),
    };
    setMessages((prev) => [...prev, userMessage]);

    try {
      const response = await fetch(`${hostname}/public/chat/${agent.id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          message: text,
          sessionId: sessionId,
          contactInfo: contactFormSubmitted ? contactInfo : null,
          channel: "webpage",
          streaming: true, // Enable streaming for sentence-by-sentence TTS
          includeAudio: true,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to send message");
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error("No response body");
      }

      const decoder = new TextDecoder();
      let buffer = "";
      let responseText = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // SSE events are separated by double newlines
        const events = buffer.split("\n\n");
        // Keep the last incomplete event in the buffer
        buffer = events.pop() || "";

        for (const eventBlock of events) {
          if (!eventBlock.trim()) continue;

          const lines = eventBlock.split("\n");
          let currentEvent = "";
          let dataLine = "";

          for (const line of lines) {
            if (line.startsWith("event:")) {
              currentEvent = line.substring(6).trim();
            } else if (line.startsWith("data:")) {
              dataLine = line.substring(5).trim();
            }
          }

          if (!dataLine) continue;

          try {
            const jsonData = JSON.parse(dataLine);

            if (currentEvent === "chunk") {
              // Handle streaming text chunk - accumulate for display
              responseText += jsonData.chunk || "";
              // Update message in real-time
              setMessages((prev) => {
                const lastMsg = prev[prev.length - 1];
                if (lastMsg && lastMsg.role === "assistant" && lastMsg.isStreaming) {
                  return prev.map((msg, idx) =>
                    idx === prev.length - 1
                      ? { ...msg, content: responseText }
                      : msg
                  );
                } else {
                  // Create new streaming message
                  return [
                    ...prev,
                    {
                      id: `assistant_${Date.now()}`,
                      role: "assistant",
                      content: responseText,
                      timestamp: Date.now(),
                      isStreaming: true,
                    },
                  ];
                }
              });
            } else if (currentEvent === "audio" && jsonData.audio) {
              // Handle audio chunk - decode and queue for playback
              try {
                const audioData = atob(jsonData.audio);
                const audioBytes = new Uint8Array(audioData.length);
                for (let i = 0; i < audioData.length; i++) {
                  audioBytes[i] = audioData.charCodeAt(i);
                }
                const format = jsonData.format || "mp3";
                const audioBlob = new Blob([audioBytes], { type: `audio/${format}` });
                const audioUrl = URL.createObjectURL(audioBlob);

                audioQueueRef.current.push({ url: audioUrl, text: jsonData.text || "" });

                // Start playing if not already
                if (!isPlayingAudioRef.current) {
                  isPlayingAudioRef.current = true;
                  playNextAudio();
                }
              } catch (audioErr) {
                console.error("Error processing audio:", audioErr);
              }
            } else if (currentEvent === "message") {
              // Streaming complete - finalize message
              setMessages((prev) => {
                const lastMsg = prev[prev.length - 1];
                if (lastMsg && lastMsg.role === "assistant") {
                  return prev.map((msg, idx) =>
                    idx === prev.length - 1
                      ? { ...msg, content: responseText || lastMsg.content, isStreaming: false }
                      : msg
                  );
                } else if (jsonData.response) {
                  // Non-streaming fallback
                  return [
                    ...prev,
                    {
                      id: `assistant_${Date.now()}`,
                      role: "assistant",
                      content: jsonData.response,
                      timestamp: Date.now(),
                    },
                  ];
                }
                return prev;
              });
            } else if (currentEvent === "done") {
              // Finalize any streaming message
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.isStreaming ? { ...msg, isStreaming: false } : msg
                )
              );
            }
          } catch (e) {
            // Failed to parse JSON - might be incomplete data
            console.error("Failed to parse SSE data:", e);
          }
        }
      }
    } catch (err) {
      console.error("Error sending voice message:", err);
      const errorMessage: Message = {
        id: `error_${Date.now()}`,
        role: "assistant",
        content: "Sorry, I couldn't process your message. Please try again.",
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsProcessing(false);
      isVoiceProcessingRef.current = false;
      // If audio is not playing, restart dictation
      if (isVoiceChatActiveRef.current && !isPlayingAudioRef.current) {
        startVoiceDictationRef.current();
      }
    }
  };

  // Toggle voice chat mode
  const toggleVoiceChat = () => {
    if (isVoiceChatActive) {
      // Stop voice chat
      setIsVoiceChatActive(false);
      isVoiceChatActiveRef.current = false;
      stopVoiceDictationRef.current();
      // Stop any playing audio
      if (audioPlayerRef.current) {
        audioPlayerRef.current.pause();
        audioPlayerRef.current = null;
      }
      // Clear audio queue
      audioQueueRef.current.forEach((item) => URL.revokeObjectURL(item.url));
      audioQueueRef.current = [];
      isPlayingAudioRef.current = false;
    } else {
      // Start voice chat
      setIsVoiceChatActive(true);
      isVoiceChatActiveRef.current = true;
      startVoiceDictationRef.current();
    }
  };

  // Cleanup voice chat on unmount
  useEffect(() => {
    return () => {
      if (isVoiceChatActiveRef.current) {
        stopVoiceDictationRef.current();
        if (audioPlayerRef.current) {
          audioPlayerRef.current.pause();
          audioPlayerRef.current = null;
        }
        audioQueueRef.current.forEach((item) => URL.revokeObjectURL(item.url));
        audioQueueRef.current = [];
      }
    };
  }, []);

  const toggleMode = () => {
    setCurrentMode(currentMode === "dark" ? "light" : "dark");
  };

  // Get logo based on whitelabel settings and dark/light mode
  const getLogo = () => {
    if (isDark && whitelabelLogos?.horizontal_logo_dark?.url) {
      return whitelabelLogos.horizontal_logo_dark.url;
    }
    if (whitelabelLogos?.horizontal_logo?.url) {
      return whitelabelLogos.horizontal_logo.url;
    }
    return getLogoMode(isDark ? "dark" : "light");
  };

  const getBrandName = () => {
    return whitelabelSettings?.title || "GTM AI";
  };

  // Get instance config (handles whitelabel and default settings)
  const instanceConfig = getInstanceConfig(currentMode, whitelabelSettings);

  // Colors matching ChatGPT exactly
  const colors = {
    bg: isDark ? "#212121" : "#ffffff",
    inputBg: isDark ? "#303030" : "#f4f4f4",
    inputBorder: isDark ? "#303030" : "#e5e5e5",
    userBubble: isDark ? "#303030" : "#f4f4f4",
    text: isDark ? "#ececec" : "#0d0d0d",
    textSecondary: isDark ? "#9b9b9b" : "#666666",
    sendButton: isDark ? "#676767" : "#d9d9d9",
    sendButtonActive: isDark ? "#ffffff" : "#0d0d0d",
    primary: instanceConfig.primaryColor,
  };

  // Generate or restore session ID and contact info
  useEffect(() => {
    if (!shortCode) return;

    const sessionKey = `ai-agent-session-${shortCode}`;
    const contactKey = `ai-agent-contact-${shortCode}`;
    const storedSession = localStorage.getItem(sessionKey);
    const storedContact = localStorage.getItem(contactKey);

    // Restore contact info (persists independently of session)
    if (storedContact) {
      try {
        const contact = JSON.parse(storedContact);
        setContactInfo(contact);
        setContactFormSubmitted(true);
      } catch (e) {
        // Invalid contact data
      }
    }

    if (storedSession) {
      try {
        const session = JSON.parse(storedSession);
        if (Date.now() - session.created < 24 * 60 * 60 * 1000) {
          setSessionId(session.id);
          const historyKey = `ai-agent-history-${shortCode}`;
          const storedHistory = localStorage.getItem(historyKey);
          if (storedHistory) {
            setMessages(JSON.parse(storedHistory));
          }
          return;
        }
      } catch (e) {
        // Invalid session data
      }
    }

    const newSession = {
      id: `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      created: Date.now(),
    };
    localStorage.setItem(sessionKey, JSON.stringify(newSession));
    setSessionId(newSession.id);
    setMessages([]);
  }, [shortCode]);

  // Fetch agent config
  useEffect(() => {
    if (!shortCode) return;

    const fetchAgent = async () => {
      try {
        const response = await axios.get(
          `${hostname}/public/agents/code/${shortCode}`
        );
        const agentData = response.data;

        if (!agentData.isActive) {
          setError("This agent is currently unavailable.");
          setLoading(false);
          return;
        }

        // For shareable webpage, check the webpage channel setting (not website/widget)
        const webpageSettings = agentData.channelSettings?.webpage;
        if (webpageSettings?.enabled === false) {
          setError("This agent's webpage has been disabled by the owner.");
          setLoading(false);
          return;
        }

        setAgent(agentData);
        setLoading(false);
      } catch (err) {
        console.error("Failed to fetch agent:", err);
        setError("Agent not found.");
        setLoading(false);
      }
    };

    fetchAgent();
  }, [shortCode]);

  // Save messages to localStorage
  useEffect(() => {
    if (shortCode && messages.length > 0) {
      const historyKey = `ai-agent-history-${shortCode}`;
      localStorage.setItem(historyKey, JSON.stringify(messages));
    }
  }, [messages, shortCode]);

  // Scroll to bottom when messages change (skip during streaming - handled separately)
  useEffect(() => {
    // Skip scroll during streaming - it's handled directly in the streaming handler
    if (isStreamingRef.current) {
      return;
    }
    // Use scrollTop on container to avoid scrolling parent page when embedded
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [messages, isProcessing]);

  // Scroll to bottom after initial load completes
  useEffect(() => {
    if (!loading && messages.length > 0) {
      // Use requestAnimationFrame to ensure DOM has rendered
      requestAnimationFrame(() => {
        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
      });
    }
  }, [loading]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`;
    }
  }, [inputValue]);

  // Check if description text overflows (needs clamping)
  useEffect(() => {
    const checkOverflow = () => {
      if (descriptionRef.current && !descriptionExpanded) {
        const element = descriptionRef.current;
        // Compare scrollHeight to clientHeight to detect overflow
        // scrollHeight includes content that overflows, clientHeight is the visible height
        const isOverflowing = element.scrollHeight > element.clientHeight;
        setDescriptionOverflows(isOverflowing);
      }
    };

    // Run after a short delay to ensure rendering is complete
    const timeoutId = setTimeout(checkOverflow, 100);
    // Also check on window resize
    window.addEventListener("resize", checkOverflow);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", checkOverflow);
    };
  }, [agent?.channelSettings?.webpage?.description, descriptionExpanded, loading]);

  const handleStopGeneration = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsProcessing(false);
    }
  };

  const handleResetConversation = () => {
    // Clear messages
    setMessages([]);
    setInputValue("");
    setAttachments([]);

    // Create new session
    if (shortCode) {
      const newSession = {
        id: `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        created: Date.now(),
      };
      const sessionKey = `ai-agent-session-${shortCode}`;
      localStorage.setItem(sessionKey, JSON.stringify(newSession));
      setSessionId(newSession.id);

      // Clear chat history
      const historyKey = `ai-agent-history-${shortCode}`;
      localStorage.removeItem(historyKey);
    }
  };

  const handleContactFormSubmit = () => {
    if (!shortCode) return;

    // Check if all required fields are filled
    const requiredFields = agent?.leadFields?.filter((f) => f.required) || [];
    const missingFields = requiredFields.filter(
      (f) => !contactInfo[f.key]?.trim()
    );

    if (missingFields.length > 0) {
      return; // Don't submit if required fields are missing
    }

    // Save contact info to localStorage (persists across chat resets)
    const contactKey = `ai-agent-contact-${shortCode}`;
    localStorage.setItem(contactKey, JSON.stringify(contactInfo));
    setContactFormSubmitted(true);
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || !agent || !sessionId || isProcessing) return;

    const userMessage: Message = {
      id: `user_${Date.now()}`,
      role: "user",
      content: inputValue,
      timestamp: Date.now(),
      attachments: attachments.map((a) => ({ name: a.name, type: a.type })),
    };

    setMessages((prev) => [...prev, userMessage]);
    const messageText = inputValue;
    setInputValue("");
    setIsProcessing(true);

    const messageAttachments =
      attachments.length > 0
        ? attachments.map((a) => ({
            name: a.name,
            content: a.content,
            type: a.type,
          }))
        : null;
    setAttachments([]);

    abortControllerRef.current = new AbortController();

    // Get streaming and multi-message settings
    const webpageSettings = agent.channelSettings?.webpage || {};
    const streamingEnabled = webpageSettings.streamingEnabled || false;

    try {
      const response = await fetch(`${hostname}/public/chat/${agent.id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          message: messageText,
          sessionId: sessionId,
          contactInfo: contactFormSubmitted ? contactInfo : null,
          attachments: messageAttachments,
          channel: "webpage",
          streaming: streamingEnabled,
        }),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        throw new Error("Failed to send message");
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error("No response body");
      }

      const decoder = new TextDecoder();
      let buffer = "";
      let streamingContent = "";
      let streamingMessageId: string | null = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        let currentEvent = "";
        for (const line of lines) {
          const trimmedLine = line.trim();

          if (trimmedLine.startsWith("event:")) {
            currentEvent = trimmedLine.substring(6).trim();
          } else if (trimmedLine.startsWith("data:")) {
            const data = trimmedLine.substring(5).trim();

            try {
              const jsonData = JSON.parse(data);

              if (currentEvent === "chunk" && streamingEnabled) {
                // Handle streaming chunk
                if (!streamingMessageId) {
                  streamingMessageId = `assistant_${Date.now()}`;
                  isStreamingRef.current = true;
                  const streamingMessage: Message = {
                    id: streamingMessageId,
                    role: "assistant",
                    content: "",
                    timestamp: Date.now(),
                    isStreaming: true,
                  };
                  setMessages((prev) => [...prev, streamingMessage]);
                }

                // Accumulate content
                streamingContent += jsonData.chunk;

                // Update DOM directly for smoother streaming (avoid React re-render on every chunk)
                if (streamingContentRef.current) {
                  streamingContentRef.current.textContent = streamingContent;
                  // Smooth scroll to bottom using scrollTop (less jerky than scrollIntoView)
                  if (messagesContainerRef.current) {
                    messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
                  }
                }
              } else if (currentEvent === "message") {
                // Message event - handles both streaming completion and non-streaming responses
                if (streamingEnabled) {
                  // Streaming mode: finalize the streaming message with full content
                  isStreamingRef.current = false;
                  if (streamingMessageId && streamingContent) {
                    setMessages((prev) =>
                      prev.map((msg) =>
                        msg.id === streamingMessageId
                          ? { ...msg, content: streamingContent, isStreaming: false }
                          : msg
                      )
                    );
                  }
                  streamingMessageId = null;
                  streamingContent = "";
                } else if (jsonData.response) {
                  // Non-streaming mode: message contains the full response
                  const assistantMessage: Message = {
                    id: `assistant_${Date.now()}`,
                    role: "assistant",
                    content: jsonData.response,
                    timestamp: Date.now(),
                  };
                  setMessages((prev) => [...prev, assistantMessage]);
                }
              } else if (currentEvent === "done") {
                // Done event - streaming is complete
                isStreamingRef.current = false;
                if (streamingMessageId && streamingContent) {
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === streamingMessageId
                        ? { ...msg, content: streamingContent, isStreaming: false }
                        : msg
                    )
                  );
                }
                streamingMessageId = null;
                streamingContent = "";
              } else if (currentEvent === "error") {
                // Remove streaming message on error
                if (streamingMessageId) {
                  setMessages((prev) =>
                    prev.filter((msg) => msg.id !== streamingMessageId)
                  );
                }
                const errorMessage: Message = {
                  id: `error_${Date.now()}`,
                  role: "assistant",
                  content:
                    jsonData.error || "Sorry, I encountered an error.",
                  timestamp: Date.now(),
                };
                setMessages((prev) => [...prev, errorMessage]);
              }
            } catch (e) {
              // Failed to parse JSON
            }
          }
        }
      }
    } catch (err: any) {
      if (err.name === "AbortError") {
        return;
      }
      console.error("Error sending message:", err);
      const errorMessage: Message = {
        id: `error_${Date.now()}`,
        role: "assistant",
        content: "Sorry, I couldn't send your message. Please try again.",
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsProcessing(false);
      abortControllerRef.current = null;
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    for (const file of Array.from(files)) {
      if (!isAllowedDocumentType(file)) {
        continue;
      }

      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64Content = event.target?.result as string;

        if (file.type !== "text/plain") {
          try {
            const formData = new FormData();
            formData.append("files", file);

            const response = await axios.post(
              `${hostname}/public/documents/convert`,
              formData,
              {
                headers: {
                  "Content-Type": "multipart/form-data",
                },
              }
            );

            // Extract content from all documents in the response
            const documents = response.data.documents || [];
            const extractedContent = documents
              .map((doc: { content?: string }) => doc.content || "")
              .join("\n\n");

            setAttachments((prev) => [
              ...prev,
              {
                name: file.name,
                content: extractedContent,
                type: file.type,
              },
            ]);
          } catch (err) {
            console.error("Failed to convert document:", err);
          }
        } else {
          setAttachments((prev) => [
            ...prev,
            {
              name: file.name,
              content: base64Content.split(",")[1] || base64Content,
              type: file.type,
            },
          ]);
        }
      };
      reader.readAsDataURL(file);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const placeholder =
    agent?.channelSettings?.webpage?.placeholder ||
    agent?.channelSettings?.website?.placeholder ||
    agent?.placementSettings?.placeholder ||
    "Type your message...";

  // Input box JSX - rendered inline to avoid focus loss on re-render
  const inputBoxContent = (
    <Box sx={{ width: "100%", maxWidth: 900, mx: "auto" }}>
      {/* Attachments preview */}
      {attachments.length > 0 && (
        <Box sx={{ mb: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
          {attachments.map((att, idx) => (
            <Box
              key={idx}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                bgcolor: colors.inputBg,
                px: 1.5,
                py: 0.75,
                borderRadius: 2,
                fontSize: 13,
                color: colors.text,
              }}
            >
              <AttachFile sx={{ fontSize: 16, color: colors.textSecondary }} />
              {att.name}
              <IconButton
                size="small"
                onClick={() => removeAttachment(idx)}
                sx={{ ml: 0.5, p: 0.25 }}
              >
                <Close sx={{ fontSize: 16, color: colors.textSecondary }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      {/* Input container */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          bgcolor: colors.inputBg,
          borderRadius: "28px",
          p: 1,
          pl: 1,
          minHeight: 60,
        }}
      >
        {/* Plus/Attach button */}
        <IconButton
          size="small"
          onClick={() => fileInputRef.current?.click()}
          disabled={isProcessing || isVoiceChatActive}
          sx={{
            color: colors.text,
            width: 44,
            height: 44,
            ml: 0.5,
            flexShrink: 0,
            "&:hover": {
              bgcolor: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)",
            },
          }}
        >
          <Add sx={{ fontSize: 26 }} />
        </IconButton>

        {/* Textarea */}
        <textarea
          ref={textareaRef}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={isVoiceChatActive ? "Voice chat active - speak to interact" : placeholder}
          disabled={isProcessing || isVoiceChatActive}
          rows={1}
          style={{
            flex: 1,
            border: "none",
            outline: "none",
            resize: "none",
            background: "transparent",
            color: colors.text,
            fontSize: 18,
            lineHeight: 1.5,
            padding: "8px 16px",
            maxHeight: 200,
            fontFamily: "inherit",
          }}
        />

        {/* Dictation button */}
        <Tooltip title={isDictating ? "Stop dictation" : "Start dictation"}>
          <span>
            <IconButton
              size="small"
              onClick={isDictating ? stopDictation : startDictation}
              disabled={isDictationLoading || isProcessing || isVoiceChatActive}
              sx={{
                color: isDictating ? "#ef4444" : colors.text,
                width: 44,
                height: 44,
                mr: 0.5,
                flexShrink: 0,
                animation: isDictating ? "pulse 1.5s infinite" : "none",
                "@keyframes pulse": {
                  "0%": { opacity: 1 },
                  "50%": { opacity: 0.5 },
                  "100%": { opacity: 1 },
                },
                "&:hover": {
                  bgcolor: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)",
                },
              }}
            >
              {isDictationLoading ? (
                <CircularProgress size={20} />
              ) : isDictating ? (
                <Stop sx={{ fontSize: 24 }} />
              ) : (
                <Mic sx={{ fontSize: 24 }} />
              )}
            </IconButton>
          </span>
        </Tooltip>

        {/* Send/Stop/Voice button */}
        {isProcessing ? (
          <IconButton
            onClick={handleStopGeneration}
            sx={{
              bgcolor: colors.sendButtonActive,
              color: colors.bg,
              width: 44,
              height: 44,
              mr: 0.5,
              flexShrink: 0,
              "&:hover": {
                bgcolor: colors.sendButtonActive,
                opacity: 0.8,
              },
            }}
          >
            <StopCircleOutlined sx={{ fontSize: 24 }} />
          </IconButton>
        ) : inputValue.trim() ? (
          <IconButton
            onClick={handleSendMessage}
            disabled={isDictating || isVoiceChatActive}
            sx={{
              bgcolor: colors.sendButtonActive,
              color: colors.bg,
              width: 44,
              height: 44,
              mr: 0.5,
              flexShrink: 0,
              "&:hover": {
                bgcolor: colors.sendButtonActive,
                opacity: 0.8,
              },
              "&:disabled": {
                bgcolor: colors.sendButton,
                color: colors.textSecondary,
              },
            }}
          >
            <ArrowUpward sx={{ fontSize: 24 }} />
          </IconButton>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mr: 0.5 }}>
            {isVoiceChatAvailable && (
              <>
                <Tooltip title={isVoiceChatActive ? "End voice chat" : "Start voice chat"}>
                  <span>
                    <IconButton
                      onClick={toggleVoiceChat}
                      disabled={isDictating}
                      sx={{
                        backgroundColor: isVoiceChatActive
                          ? isDark
                            ? "rgba(255,255,255,0.1)"
                            : "rgba(0,0,0,0.05)"
                          : colors.sendButtonActive,
                        color: isVoiceChatActive
                          ? colors.textSecondary
                          : colors.bg,
                        width: 44,
                        height: 44,
                        animation: isVoiceRecording ? "voicePulse 1.5s infinite" : "none",
                        "@keyframes voicePulse": {
                          "0%": { opacity: 1 },
                          "50%": { opacity: 0.6 },
                          "100%": { opacity: 1 },
                        },
                        "&:hover": {
                          backgroundColor: isVoiceChatActive
                            ? isDark
                              ? "rgba(255,255,255,0.15)"
                              : "rgba(0,0,0,0.08)"
                            : colors.sendButtonActive,
                          opacity: isVoiceChatActive ? 1 : 0.8,
                        },
                        "&:disabled": {
                          bgcolor: colors.sendButton,
                          color: colors.textSecondary,
                        },
                      }}
                    >
                      <GraphicEq sx={{ fontSize: 24 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            )}
            {!isVoiceChatAvailable && (
              <IconButton
                disabled
                sx={{
                  bgcolor: colors.sendButton,
                  color: colors.textSecondary,
                  width: 44,
                  height: 44,
                }}
              >
                <ArrowUpward sx={{ fontSize: 24 }} />
              </IconButton>
            )}
          </Box>
        )}
      </Box>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={DOCUMENT_ACCEPT_TYPES}
        multiple
        style={{ display: "none" }}
        onChange={handleFileSelect}
      />

      {/* Powered by footer */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.75,
          mt: 1.5,
        }}
      >
        <Typography
          sx={{
            fontSize: 12,
            color: colors.textSecondary,
          }}
        >
          Powered by
        </Typography>
        <Box
          component="a"
          href={whitelabelSettings?.webDomain ? `https://${whitelabelSettings.webDomain}` : `https://${window.location.hostname}`}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: "flex",
            alignItems: "center",
            textDecoration: "none",
            "&:hover": {
              opacity: 0.8,
            },
          }}
        >
          <img
            src={getLogo()}
            alt={getBrandName()}
            style={{
              height: 18,
              objectFit: "contain",
            }}
          />
        </Box>
      </Box>
    </Box>
  );

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          bgcolor: colors.bg,
        }}
      >
        <CircularProgress sx={{ color: colors.textSecondary }} />
      </Box>
    );
  }

  if (error || !agent) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          bgcolor: colors.bg,
          p: 3,
        }}
      >
        <Typography variant="h5" sx={{ color: colors.text, mb: 1 }}>
          {error || "Agent not found"}
        </Typography>
        <Typography sx={{ color: colors.textSecondary }}>
          The agent you're looking for may have been removed or is unavailable.
        </Typography>
      </Box>
    );
  }

  const showWelcome = messages.length === 0;
  const needsContactForm =
    agent?.channelSettings?.webpage?.contactForm &&
    agent?.leadFields &&
    agent.leadFields.length > 0 &&
    !contactFormSubmitted;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        bgcolor: colors.bg,
      }}
    >
      {/* Header with agent name and photo */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          py: 1.5,
          px: 2,
          flexShrink: 0,
          borderBottom: `1px solid ${isDark ? "#303030" : "#ebebeb"}`,
          position: "sticky",
          top: 0,
          zIndex: 10,
          bgcolor: colors.bg,
        }}
      >
        {/* Left spacer for centering */}
        <Box sx={{ width: 40 }} />

        {/* Center - Agent info */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            {agent.avatarImage ? (
              <Box
                component="img"
                src={agent.avatarImage}
                alt={agent.name}
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
              />
            ) : (
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "50%",
                  bgcolor: "#10a37f",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontSize: 18,
                  fontWeight: 600,
                }}
              >
                {agent.name.charAt(0).toUpperCase()}
              </Box>
            )}
            <Typography
              sx={{
                color: colors.text,
                fontSize: 20,
                fontWeight: 600,
              }}
            >
              {agent.name}
            </Typography>
          </Box>
          {/* Description below agent name */}
          {agent.channelSettings?.webpage?.description && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                maxWidth: 900,
                width: "100%",
                px: 2,
              }}
            >
              <Box
                ref={descriptionRef}
                sx={{
                  color: colors.textSecondary,
                  fontSize: 13,
                  textAlign: "center",
                  "& a": {
                    color: colors.primary,
                  },
                  "& p": {
                    m: 0,
                  },
                  ...(!descriptionExpanded && {
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }),
                }}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(agent.channelSettings.webpage.description),
                }}
              />
              {(descriptionOverflows || descriptionExpanded) && (
                <IconButton
                  size="small"
                  onClick={() => setDescriptionExpanded(!descriptionExpanded)}
                  sx={{
                    color: colors.textSecondary,
                    p: 0,
                    mt: 0.25,
                    "&:hover": {
                      color: colors.primary,
                      bgcolor: "transparent",
                    },
                  }}
                >
                  {descriptionExpanded ? (
                    <KeyboardArrowUp sx={{ fontSize: 20 }} />
                  ) : (
                    <KeyboardArrowDown sx={{ fontSize: 20 }} />
                  )}
                </IconButton>
              )}
            </Box>
          )}
        </Box>

        {/* Right - Mode toggle and Reset conversation buttons */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Tooltip title={isDark ? "Switch to light mode" : "Switch to dark mode"} arrow>
            <IconButton
              onClick={toggleMode}
              sx={{
                color: colors.text,
                width: 40,
                height: 40,
                "&:hover": {
                  bgcolor: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)",
                },
              }}
            >
              {isDark ? (
                <LightModeOutlined sx={{ fontSize: 22 }} />
              ) : (
                <DarkModeOutlined sx={{ fontSize: 22 }} />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="Start new conversation" arrow>
            <span>
              <IconButton
                onClick={handleResetConversation}
                disabled={messages.length === 0}
                sx={{
                  color: colors.text,
                  width: 40,
                  height: 40,
                  "&:hover": {
                    bgcolor: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)",
                  },
                  "&:disabled": {
                    color: colors.textSecondary,
                    opacity: 0.5,
                  },
                }}
              >
                <RefreshOutlined sx={{ fontSize: 22 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {showWelcome ? (
        /* Welcome Screen - Input centered in the middle */
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            px: 3,
            pb: 10,
          }}
        >
          <Typography
            sx={{
              color: colors.text,
              fontSize: isMobile ? 24 : 32,
              fontWeight: 600,
              mb: 4,
              textAlign: "center",
            }}
          >
            {agent.channelSettings?.webpage?.welcomeMessage ||
              agent.channelSettings?.website?.welcomeMessage ||
              agent.placementSettings?.welcomeMessage ||
              "Hello! How can I help you today?"}
          </Typography>

          {needsContactForm ? (
            /* Contact Form */
            <Box sx={{ width: "100%", maxWidth: 500, px: isMobile ? 0 : 2 }}>
              <Box
                sx={{
                  bgcolor: colors.inputBg,
                  borderRadius: 2,
                  p: 3,
                }}
              >
                <Typography
                  sx={{
                    color: colors.text,
                    fontSize: 16,
                    fontWeight: 500,
                    mb: 2,
                  }}
                >
                  Please enter your information to start chatting
                </Typography>
                {agent.leadFields?.map((field) => (
                  <TextField
                    key={field.key}
                    label={field.label}
                    type={field.type === "email" ? "email" : "text"}
                    required={field.required}
                    fullWidth
                    size="small"
                    value={contactInfo[field.key] || ""}
                    onChange={(e) =>
                      setContactInfo((prev) => ({
                        ...prev,
                        [field.key]: e.target.value,
                      }))
                    }
                    sx={{
                      mb: 2,
                      "& .MuiOutlinedInput-root": {
                        bgcolor: isDark ? "#424242" : "#fff",
                        color: colors.text,
                      },
                      "& .MuiInputLabel-root": {
                        color: colors.textSecondary,
                      },
                    }}
                  />
                ))}
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleContactFormSubmit}
                  disabled={
                    agent.leadFields?.some(
                      (f) => f.required && !contactInfo[f.key]?.trim()
                    ) || false
                  }
                  sx={{
                    bgcolor: colors.primary,
                    color: "#fff",
                    textTransform: "none",
                    "&:hover": {
                      bgcolor: colors.primary,
                      opacity: 0.9,
                    },
                  }}
                >
                  Start Chat
                </Button>
              </Box>
            </Box>
          ) : (
            <Box sx={{ width: "100%", maxWidth: 900, px: isMobile ? 0 : 2 }}>
              {inputBoxContent}
            </Box>
          )}
        </Box>
      ) : (
        /* Chat View */
        <>
          {/* Messages area */}
          <Box
            ref={messagesContainerRef}
            sx={{
              flex: 1,
              overflow: "auto",
              px: isMobile ? 2 : 4,
              py: 2,
            }}
          >
            <Box sx={{ maxWidth: 900, mx: "auto" }}>
              {messages.map((message) => (
                <Box
                  key={message.id}
                  sx={{
                    display: "flex",
                    justifyContent: message.role === "user" ? "flex-end" : "flex-start",
                    mb: 3,
                  }}
                >
                  {message.role === "user" ? (
                    /* User message - bubble on right */
                    <Box
                      sx={{
                        bgcolor: colors.userBubble,
                        color: colors.text,
                        px: 2.5,
                        py: 1.5,
                        borderRadius: "20px",
                        maxWidth: "85%",
                        fontSize: 16,
                        lineHeight: 1.5,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {message.content}
                      {message.attachments && message.attachments.length > 0 && (
                        <Box sx={{ mt: 1, display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                          {message.attachments.map((att, idx) => (
                            <Typography
                              key={idx}
                              sx={{
                                fontSize: 12,
                                color: colors.textSecondary,
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <AttachFile sx={{ fontSize: 12 }} />
                              {att.name}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </Box>
                  ) : (
                    /* Assistant message - left aligned, no bubble */
                    <Box
                      sx={{
                        color: colors.text,
                        maxWidth: "100%",
                        fontSize: 16,
                        lineHeight: 1.75,
                        whiteSpace: message.isStreaming ? "pre-wrap" : undefined,
                        "& p": { m: 0, mb: 2 },
                        "& p:last-child": { mb: 0 },
                        "& pre": {
                          bgcolor: isDark ? "#1e1e1e" : "#f6f6f6",
                          p: 2,
                          borderRadius: 1,
                          overflow: "auto",
                          fontSize: 14,
                        },
                        "& code": {
                          bgcolor: isDark ? "#1e1e1e" : "#f6f6f6",
                          px: 0.5,
                          py: 0.25,
                          borderRadius: 0.5,
                          fontSize: 14,
                          fontFamily: "monospace",
                        },
                        "& pre code": {
                          bgcolor: "transparent",
                          p: 0,
                        },
                        "& ul, & ol": {
                          pl: 3,
                          mb: 2,
                        },
                        "& li": {
                          mb: 0.5,
                        },
                        "& a": {
                          color: colors.primary,
                          textDecoration: "underline",
                        },
                        "& table": {
                          borderCollapse: "collapse",
                          width: "100%",
                          mb: 2,
                        },
                        "& th, & td": {
                          border: `1px solid ${isDark ? "#424242" : "#e5e5e5"}`,
                          p: 1,
                          textAlign: "left",
                        },
                        "& th": {
                          bgcolor: isDark ? "#2f2f2f" : "#f6f6f6",
                        },
                        "& blockquote": {
                          borderLeft: `3px solid ${isDark ? "#424242" : "#e5e5e5"}`,
                          pl: 2,
                          ml: 0,
                          color: colors.textSecondary,
                        },
                      }}
                    >
                      {message.isStreaming ? (
                        <div ref={streamingContentRef}>{message.content}</div>
                      ) : (
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {message.content}
                        </ReactMarkdown>
                      )}
                    </Box>
                  )}
                </Box>
              ))}

              {/* Typing indicator - ChatGPT-style single pulsing dot */}
              <Box sx={{ mb: 3, visibility: isProcessing ? "visible" : "hidden" }}>
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    bgcolor: colors.text,
                    animation: isProcessing
                      ? "pulse 1.2s ease-in-out infinite"
                      : "none",
                    "@keyframes pulse": {
                      "0%, 100%": {
                        transform: "scale(0.6)",
                      },
                      "50%": {
                        transform: "scale(1)",
                      },
                    },
                  }}
                />
              </Box>

              <div ref={messagesEndRef} />
            </Box>
          </Box>

          {/* Input area at bottom */}
          <Box sx={{ px: isMobile ? 2 : 4, pb: 3, pt: 1 }}>
            {inputBoxContent}
          </Box>
        </>
      )}
    </Box>
  );
};

export default ShareableAgentPage;
